import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"
import { planningMutation } from "../planning_mutation"

// ONE delegated drop controller for the whole capacity-planning board.
//
// Replaces a per-cell `planning-drop` controller: the 4-week view renders thousands of
// day cells, and instantiating a Stimulus controller (+ 4 drag listeners) on each one
// froze the page for ~30s on DOMContentLoaded. This single controller lives on the board
// container and resolves the target cell from the event via `closest`, so the cells are
// plain `[data-planning-cell]` markers with no controller or listeners of their own.
//
// Dispatches by the dragged payload's resourceType:
//   - Employee → POST to allocations (one planned person-day for plannable + date)
//   - Trade    → POST to trade_demands (one rough demand: 1x trade for plannable + date)
export default class extends Controller {
  static values = {
    url: String,
    // POST target for a fleet (vehicle/device) drop — one planned fleet for plannable + date.
    fleetUrl: String,
    // POST target for a combined vehicle+driver drop — one fleet AND one employee allocation.
    fleetCrewUrl: String,
    tradeDemandUrl: String,
    // `:id` placeholder URL for moving an existing allocation (PATCH …/allocations/:id/move).
    moveUrlTemplate: String,
    // `:id` placeholder URL for moving an existing fleet allocation.
    fleetMoveUrlTemplate: String,
    // Bounds of the visible window — the fallback von..bis for a whole-site-duration drop
    // when the site has no usable start/end date.
    rangeStart: String,
    rangeEnd: String,
    // PATCH target for the manual chip order within a cell, and whether the account's sort mode
    // is the manual one at all — every other mode derives its order from the data, so dropping a
    // chip on its own cell stays the no-op it has always been.
    reorderUrl: String,
    reorderEnabled: Boolean,
  }

  connect() {
    this.activeCell = null
    this.pendingClick = null
  }

  disconnect() {
    this.clear()
    this.cancelPendingClick()
  }

  cellFor(event) {
    return event.target.closest?.("[data-planning-cell]") || null
  }

  // The site label / section-header name cell: dropping a resource there plans it across
  // the whole site duration (via the range dialog).
  siteTargetFor(event) {
    return event.target.closest?.("[data-planning-site-drop]") || null
  }

  highlight(cell) {
    if (this.activeCell === cell) return
    this.clear()
    cell?.classList.add("ring-2", "ring-inset", "ring-green-400", "bg-green-50")
    this.activeCell = cell
  }

  clear() {
    this.activeCell?.classList.remove("ring-2", "ring-inset", "ring-green-400", "bg-green-50")
    this.activeCell = null
  }

  dragover(event) {
    const cell = this.cellFor(event) || this.siteTargetFor(event)
    if (!cell) return
    // preventDefault on dragover is what allows the subsequent drop to fire.
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    this.highlight(cell)
  }

  // dragleave bubbles up from child nodes as the pointer moves between cells/chips, so only
  // clear the highlight when the pointer actually leaves the board container.
  dragleave(event) {
    if (event.relatedTarget && this.element.contains(event.relatedTarget)) return
    this.clear()
  }

  drop(event) {
    const cell = this.cellFor(event)
    const siteTarget = cell ? null : this.siteTargetFor(event)
    if (!cell && !siteTarget) return
    event.preventDefault()
    this.clear()

    try {
      const payload = event.dataTransfer.getData("application/json")
      if (!payload) return

      const data = JSON.parse(payload)
      if (!data.resourceId) return

      // A resource dropped on the site label → plan it across the whole site duration.
      // Moving an existing chip onto the label stays a no-op.
      if (siteTarget) {
        if (data.kind !== "move") this.openSiteDurationDialog(siteTarget, data)
        return
      }

      // Moving an existing allocation chip onto another cell (a different day and/or row) —
      // or, when it lands back on its own cell in the manual sort mode, reordering it there.
      if (data.kind === "move") {
        if (this.isSameCell(cell, data) && data.resourceType === "Employee") {
          this.reorder(cell, event, data)
        } else {
          this.move(cell, data)
        }
        return
      }

      // Aggregated period cell (week/month/quarter): don't plan a single day directly — open the
      // dialog so the user confirms / narrows the von..bis range (pre-filled to the period).
      if (cell.dataset.cellAggregated === "true") {
        this.openRangeDialog(cell, data, "fixed")
        return
      }

      const context = {
        plannable_type: cell.dataset.cellPlannableType,
        plannable_id: Number(cell.dataset.cellPlannableId),
        date: cell.dataset.cellDate,
      }

      if (data.resourceType === "Employee") {
        // Multi-select drop → one allocation per employee in this cell (the controller skips
        // any already planned here) and clear the panel selection on success.
        if (data.resourceIds?.length > 1) {
          this.postMulti(this.urlValue, { employee_ids: data.resourceIds, ...context })
        } else {
          this.post(this.urlValue, { employee_id: data.resourceId, ...context }, "createAllocation")
        }
      } else if (data.resourceType === "Team") {
        // Whole-team drop → the server resolves the members and creates one allocation per
        // plannable member (already-planned members are skipped). Plain post, not postMulti —
        // a team drop must not clear the user's ticked employee multi-select.
        this.post(this.urlValue, { team_id: data.resourceId, ...context }, "createTeamAllocations")
      } else if (data.resourceType === "Trade") {
        if (!this.tradeDemandUrlValue) return
        this.post(
          this.tradeDemandUrlValue,
          { trade_id: data.resourceId, required_count: 1, ...context },
          "createTradeDemand",
        )
      } else if (data.resourceType === "Fleet") {
        if (!this.fleetUrlValue) return
        if (data.resourceIds?.length > 1) {
          this.post(
            this.fleetUrlValue,
            { fleet_ids: data.resourceIds, ...context },
            "createFleetAllocations",
          )
        } else {
          this.post(
            this.fleetUrlValue,
            { fleet_id: data.resourceId, ...context },
            "createFleetAllocation",
          )
        }
      } else if (data.resourceType === "FleetCrew") {
        if (!this.fleetCrewUrlValue) return
        this.post(
          this.fleetCrewUrlValue,
          { fleet_id: data.fleetId, employee_id: data.employeeId, ...context },
          "createFleetCrewAllocation",
        )
      }
    } catch (error) {
      Honeybadger.notify(error, {
        context: { component: "planning-board-drop", action: "drop" },
      })
    }
  }

  // Click-to-add: clicking a cell's background opens the dialog in "pick" mode (the user chooses
  // the resource). Interactive children (chips, their buttons/forms, the corner drill link, inputs)
  // are skipped so their own actions keep working.
  click(event) {
    // A chip's ✕ → open the styled remove-confirm dialog (replaces the old form / browser confirm).
    const removeBtn = event.target.closest("[data-planning-remove]")
    if (removeBtn) {
      event.preventDefault()
      this.cancelPendingClick()
      document.dispatchEvent(
        new CustomEvent("planning:confirm-remove", {
          detail: {
            url: removeBtn.dataset.removeUrl,
            title: removeBtn.dataset.removeTitle,
            body: removeBtn.dataset.removeBody,
          },
        }),
      )
      return
    }

    // A demand chip's note button → the note editor (Planning::DemandNoteDialogComponent).
    const noteBtn = event.target.closest("[data-planning-demand-note]")
    if (noteBtn) {
      event.preventDefault()
      this.cancelPendingClick()
      document.dispatchEvent(
        new CustomEvent("planning:edit-demand-note", {
          detail: { url: noteBtn.dataset.noteUrl, text: noteBtn.dataset.noteText },
        }),
      )
      return
    }

    if (
      event.target.closest(
        "button, a, form, input, select, [data-controller~='planning-trade-fill']",
      )
    )
      return
    const cell = this.cellFor(event)
    if (!cell) return

    // A click on an employee chip edits THAT day ("Baustelle ändern") instead of planning a new
    // one. Masked chips carry no employee and stay inert.
    const chip = event.target.closest("[data-chip-allocation-id][data-chip-employee-id]")
    if (chip) return this.openChipEdit(chip, cell)

    // Aggregated cells also support double-click → drill, so defer the single-click "add" briefly
    // and cancel it if a double-click follows. Day cells (no drill url) open immediately.
    if (cell.dataset.cellDrillUrl) {
      this.cancelPendingClick()
      this.pendingClick = setTimeout(() => {
        this.pendingClick = null
        this.openRangeDialog(cell, null, "pick")
      }, 220)
      return
    }

    this.openRangeDialog(cell, null, "pick")
  }

  // The chip's day as a one-day run: set_span/retarget_span with original_from == original_to.
  // A label-only edit narrows to the chip's own row (relabel_span with allocation_id).
  openChipEdit(chip, cell) {
    const date = cell.dataset.cellDate
    document.dispatchEvent(
      new CustomEvent("planning:plan-range", {
        detail: {
          mode: "plannablePick",
          editMode: true,
          originalFrom: date,
          originalTo: date,
          employeeId: Number(chip.dataset.chipEmployeeId),
          employeeName: chip.dataset.chipEmployeeName,
          plannableType: cell.dataset.cellPlannableType,
          plannableId: Number(cell.dataset.cellPlannableId),
          plannableName: cell.dataset.cellPlannableName || "",
          periodStart: date,
          periodEnd: date,
          label: chip.dataset.chipLabel || "",
          allocationId: Number(chip.dataset.chipAllocationId),
        },
      }),
    )
  }

  // Double-click an aggregated cell → drill into the finer view (also available via the corner icon).
  dblclick(event) {
    const cell = this.cellFor(event)
    if (!cell || !cell.dataset.cellDrillUrl) return
    this.cancelPendingClick()
    window.location = cell.dataset.cellDrillUrl
  }

  cancelPendingClick() {
    if (this.pendingClick) {
      clearTimeout(this.pendingClick)
      this.pendingClick = null
    }
  }

  // Bounds passed to the dialog: a day cell is a single day; an aggregated cell is its period.
  periodBoundsFor(cell) {
    if (cell.dataset.cellAggregated === "true") {
      return { start: cell.dataset.cellPeriodStart, end: cell.dataset.cellPeriodEnd }
    }
    return { start: cell.dataset.cellDate, end: cell.dataset.cellDate }
  }

  // Open the planning-allocation-dialog. `mode` is "fixed" (resource pre-chosen by a drop) or
  // "pick" (the dialog shows a resource picker). `data` is the dragged payload, or null for pick.
  openRangeDialog(cell, data, mode) {
    const bounds = this.periodBoundsFor(cell)
    document.dispatchEvent(
      new CustomEvent("planning:plan-range", {
        detail: {
          mode: mode,
          resourceType: data?.resourceType,
          resourceId: data?.resourceId,
          resourceIds: data?.resourceIds,
          resourceName: data?.resourceName || "",
          fleetId: data?.fleetId,
          employeeId: data?.employeeId,
          plannableType: cell.dataset.cellPlannableType,
          plannableId: cell.dataset.cellPlannableId,
          plannableName: cell.dataset.cellPlannableName || "",
          periodStart: bounds.start,
          periodEnd: bounds.end,
        },
      }),
    )
  }

  // Open the dialog prefilled with the site's construction period (falling back to the visible
  // window for missing bounds) and the "Nur Arbeitstage" option pre-checked, so one drop plans
  // the resource across the whole site duration.
  openSiteDurationDialog(target, data) {
    document.dispatchEvent(
      new CustomEvent("planning:plan-range", {
        detail: {
          mode: "fixed",
          resourceType: data.resourceType,
          resourceId: data.resourceId,
          resourceIds: data.resourceIds,
          resourceName: data.resourceName || "",
          fleetId: data.fleetId,
          employeeId: data.employeeId,
          plannableType: "Site",
          plannableId: target.dataset.siteId,
          plannableName: target.dataset.siteName || "",
          periodStart: target.dataset.siteStart || this.rangeStartValue,
          periodEnd: target.dataset.siteEnd || this.rangeEndValue,
          workingDaysOnly: true,
        },
      }),
    )
  }

  // Move an existing allocation chip to the dropped-on cell. Chips render only in day cells, so
  // an aggregated target is ignored; a drop back onto the chip's own cell is a no-op. Employee and
  // fleet chips share the same move body but post to their own endpoints (chosen by resourceType).
  move(cell, data) {
    const template =
      data.resourceType === "Fleet" ? this.fleetMoveUrlTemplateValue : this.moveUrlTemplateValue
    if (cell.dataset.cellAggregated === "true" || !data.allocationId || !template) return

    const plannableType = cell.dataset.cellPlannableType
    const plannableId = cell.dataset.cellPlannableId
    const date = cell.dataset.cellDate

    if (
      plannableType === data.sourcePlannableType &&
      plannableId === String(data.sourcePlannableId) &&
      date === data.sourceDate
    )
      return

    const url = template.replace("__ID__", data.allocationId)
    this.post(
      url,
      { plannable_type: plannableType, plannable_id: Number(plannableId), date: date },
      "moveAllocation",
      "PATCH",
    )
  }

  // Whether the chip was dropped back onto the cell it came from.
  isSameCell(cell, data) {
    return (
      cell.dataset.cellPlannableType === data.sourcePlannableType &&
      cell.dataset.cellPlannableId === String(data.sourcePlannableId) &&
      cell.dataset.cellDate === data.sourceDate
    )
  }

  // Manual order within one cell group. The dragged chip is placed before or after the chip it
  // was dropped on (whichever half of it the pointer is in); dropping on the cell background or
  // on another group's chip sends it to the end of its own group. Cross-group moves are not a
  // reorder — the chip would have to change which demand it fulfils.
  reorder(cell, event, data) {
    if (!this.reorderEnabledValue || !this.reorderUrlValue) return
    if (cell.dataset.cellAggregated === "true" || !data.allocationId) return

    const group = data.sourceTradeDemandId || ""
    const chips = Array.from(cell.querySelectorAll("[data-chip-allocation-id]")).filter(
      (chip) => (chip.dataset.chipDemandId || "") === group,
    )
    const current = chips.map((chip) => chip.dataset.chipAllocationId)
    const dragged = String(data.allocationId)
    if (!current.includes(dragged)) return

    const target = event.target.closest?.("[data-chip-allocation-id]")
    const targetId = target?.dataset.chipAllocationId
    // Dropped on itself: nothing to reorder. Without this it would read as "no target" and
    // send the chip to the end of its group.
    if (targetId === dragged) return

    const rest = current.filter((id) => id !== dragged)
    let index = rest.indexOf(targetId ?? "")

    if (index === -1) {
      index = rest.length
    } else {
      const box = target.getBoundingClientRect()
      // Horizontal chip rows (the tile card size) read left/right; stacked rows read top/bottom.
      const after =
        box.width > box.height * 2
          ? event.clientX > box.left + box.width / 2
          : event.clientY > box.top + box.height / 2
      if (after) index += 1
    }

    rest.splice(index, 0, dragged)
    if (rest.join() === current.join()) return

    this.post(
      this.reorderUrlValue,
      {
        plannable_type: cell.dataset.cellPlannableType,
        plannable_id: Number(cell.dataset.cellPlannableId),
        date: cell.dataset.cellDate,
        trade_demand_id: group,
        ordered_ids: rest,
      },
      "reorderAllocations",
      "PATCH",
    )
  }

  post(url, body, action, method = "POST") {
    planningMutation({ url, method, body, component: `planning-board-drop:${action}` })
  }

  // A multi-employee create: clear the panel selection once the batch succeeds, so the ticked
  // set doesn't linger after it has been planned.
  async postMulti(url, body) {
    const { ok } = await planningMutation({
      url,
      method: "POST",
      body,
      component: "planning-board-drop:createAllocations",
    })
    if (ok) document.dispatchEvent(new CustomEvent("planning:clear-selection"))
  }
}
